import React from 'react'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Avatar from '@material-ui/core/Avatar'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import FlagIcon from 'components/material-symbol/icons/Flag'
import PlaceIcon from 'components/material-symbol/icons/Place'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import CalendarTodayIcon from 'components/material-symbol/icons/CalendarToday'
import { getInitials } from 'lib/text-display'
import { durationText } from 'utils/durationUtils'
import {
  getMemberLabel,
  getPriceLabel,
} from 'pages/schedule/SelectLessonTime/redesign/booking-summary-card'
import useChromeStyles from 'pages/schedule/SelectLessonTime/redesign/styles'
import CancellationCallout from 'pages/schedule/components/cancellation-callout'
import useStyles from './styles'

const CoachAvatar = ({ coach }) => {
  const chrome = useChromeStyles()
  const photo = coach.coachProfile?.profilePhoto
  return photo ? (
    <Avatar className={chrome.avatar} alt={coach.name} src={photo} />
  ) : (
    <Avatar className={chrome.avatar}>{getInitials(coach.name)}</Avatar>
  )
}

CoachAvatar.propTypes = { coach: PropTypes.object.isRequired }

// Step 2 flavour of the step 1 `BookingSummaryCard`: the same coach header,
// lesson facts and total (it reuses the step 1 helpers and style sheet), plus
// the full slot range in darker text and the cancellation callout under the
// total. A later consolidation could give `BookingSummaryCard` a `slotLabel`
// prop and a footer slot and delete this wrapper.
const StudentSummaryCard = ({ lessonType, coach, slotLabel }) => {
  const chrome = useChromeStyles()
  const classes = useStyles()
  const facility = lessonType.facilities?.[0]
  const memberLabel = getMemberLabel(coach.coachProfile)
  const priceLabel = getPriceLabel(lessonType)
  const cityState = [facility?.city, facility?.state].filter(Boolean).join(', ')

  return (
    <aside className={chrome.summaryCard} aria-label="Booking summary">
      <div className={chrome.coachRow}>
        <CoachAvatar coach={coach} />
        <div>
          <Typography variant="h6" component="p" className={chrome.coachName}>
            {coach.name}
          </Typography>
          {memberLabel && (
            <span className={chrome.coachTitle}>
              <Chip
                size="small"
                label={memberLabel}
                className={chrome.memberChip}
              />
            </span>
          )}
        </div>
      </div>
      <hr className={chrome.summaryDivider} />
      <Typography variant="h5" component="h2" className={chrome.lessonTitle}>
        {lessonType.title}
      </Typography>
      <ul className={chrome.facts}>
        {facility?.name && (
          <li className={chrome.fact}>
            <FlagIcon aria-hidden="true" />
            <span>{facility.name}</span>
          </li>
        )}
        {cityState && (
          <li className={chrome.fact}>
            <PlaceIcon aria-hidden="true" />
            <span>{cityState}</span>
          </li>
        )}
        <li className={chrome.fact}>
          <ScheduleIcon aria-hidden="true" />
          <span>{durationText(lessonType.durationInMinutes)}</span>
        </li>
        {slotLabel && (
          <li className={clsx(chrome.fact, classes.factStrong)}>
            <CalendarTodayIcon aria-hidden="true" />
            <span>{slotLabel}</span>
          </li>
        )}
      </ul>
      {priceLabel && (
        <>
          <hr className={chrome.summaryDivider} />
          <div className={chrome.totalRow}>
            <span className={chrome.totalLabel}>Total</span>
            <Typography
              variant="h5"
              component="span"
              className={chrome.totalValue}
            >
              {priceLabel}
            </Typography>
          </div>
        </>
      )}
      <CancellationCallout
        canStudentCancel={coach.canStudentCancel}
        cancellationPolicy={coach.cancellationPolicy}
        className={classes.summaryCallout}
      />
    </aside>
  )
}

StudentSummaryCard.propTypes = {
  lessonType: PropTypes.object.isRequired,
  coach: PropTypes.object.isRequired,
  slotLabel: PropTypes.string,
}

export default StudentSummaryCard
